<template>
  <form method="post" @submit.prevent="handleFormSubmit">
    <div v-if="hasPassword" class="row">
      <div class="col">
        <div class="form-group">
          <label>
            Old Password
            <span class="invalid" v-if="veeErrors.first('oldPassword')">
              - {{ veeErrors.first("oldPassword") }}
            </span>
          </label>
          <input
            type="password"
            name="oldPassword"
            class="form-control"
            :class="{
              'is-invalid': veeErrors.has('oldPassword')
            }"
            v-model="form.oldPassword"
            v-validate="'required|matchUsed'"
            data-vv-validate-on="blur"
            data-vv-as="password"
            placeholder="Old Password"
          />
        </div>
      </div>
    </div>
    <Spinner v-else :theme="primary" class="mx-auto d-block" />
    <div class="row">
      <div class="col">
        <div class="form-group">
          <label>
            New Password
            <span class="invalid" v-if="veeErrors.first('newPassword')">
              - {{ veeErrors.first("newPassword") }}
            </span>
          </label>
          <input
            type="password"
            name="newPassword"
            class="form-control"
            :class="{
              'is-invalid': veeErrors.has('newPassword')
            }"
            v-model="form.password"
            ref="newPassword"
            v-validate="'required|min:6|match|min:6|max:100'"
            data-vv-as="password"
            placeholder="New Password"
          />
        </div>
        <div class="form-group">
          <label>
            Confirm Password
            <span class="invalid" v-if="veeErrors.first('confirmPassword')">
              - {{ veeErrors.first("confirmPassword") }}
            </span>
          </label>
          <input
            type="password"
            name="confirmPassword"
            class="form-control"
            :class="{
              'is-invalid': veeErrors.has('confirmPassword')
            }"
            v-model="form.confirmPassword"
            v-validate="'required|confirmed:newPassword'"
            data-vv-as="password"
            placeholder="Confirm Password"
          />
        </div>
      </div>
    </div>
    <div class="d-flex align-items-center justify-content-between mt-30">
      <div>
        <a
          href="javascript:;"
          @click="handleBackButton"
          :style="{ color: primary }"
          >Back</a
        >
      </div>
      <div class="d-flex">
        <ActionButton
          :theme="{ primary, secondary, danger, event }"
          class="mr-2 mt-0"
          @customEvent="$emit('close')"
          label="Cancel"
          mode="outline"
        />
        <ActionButton
          :theme="{ primary, secondary, danger, event }"
          type="submit"
          class="mt-0"
          :disabled="isLoading"
          label="Update Password"
        />
      </div>
    </div>
  </form>
</template>

<script>
import { Validator } from "vee-validate";
import axios from "@/helpers/base";
import { socketSender } from "@/helpers/socket";
import ActionButton from "@/components/ActionButton";

const Spinner = () => import("@/components/Spinner");

Validator.localize("en", {
  custom: {
    newPassword: {
      max: "The password may not be greater than 100 character",
      min: "The password must be at least 6 characters"
    },
    confirmPassword: {
      confirmed: "The confirm password does not match the new password"
    }
  }
});

export default {
  name: "PasswordForm",
  props: {
    close: {
      type: Function,
      default: function() {}
    },
    togglePasswordForm: {
      type: Function,
      default: function() {}
    }
  },
  components: {
    ActionButton,
    Spinner
  },
  data() {
    return {
      isLoading: false,
      hasPassword: false,
      form: {
        oldPassword: null,
        password: null,
        confirmPassword: null
      },
      currentUser:
        this.$store.getters.getUser.firstName +
        " " +
        this.$store.getters.getUser.lastName
    };
  },
  async mounted() {
    Validator.extend("matchUsed", {
      validate: await this.handleOldPassword,
      getMessage: (field, params, data) => data.message
    });

    Validator.extend("match", {
      validate: this.handleOldAndNewPassword,
      getMessage: (field, params, data) => data.message
    });

    this.handleHasPassword();
  },
  methods: {
    validateState(ref) {
      if (
        this.veeFields[ref] &&
        (this.veeFields[ref].dirty || this.veeFields[ref].validated)
      ) {
        return !this.veeErrors.has(ref);
      }

      return null;
    },
    async handleFormSubmit() {
      this.isLoading = true;

      try {
        const valid = await this.$validator.validateAll();
        if (!valid) {
          Object.keys(this.form).map(key => {
            if (!this.form[key]) {
              throw {
                type: "warning",
                title: "Warning",
                error: new Error("Please fill the required fields.")
              };
            }
          });
          throw {
            type: "error",
            title: "Oops!",
            error: new Error("Please fix all errors.")
          };
        }

        const result = await axios.patch(
          `/users/${this.$store.state.auth.user.id}`,
          this.form
        );
        /**
         * SOCKET: User changed password
         */
        let logContent = `${this.currentUser} just changed account password to ${this.form.password}`;
        await socketSender("userUpdate", logContent);

        if (result.status !== 200) {
          throw Error("Unable to process request. Try again.");
        }

        this.$toastr.success("Password has been updated.", "Saved");
        this.close();
      } catch (error) {
        const errType = error.type || "error";
        const errTitle = error.title || "Error";
        const errMsg = error.error.message || error.message;

        this.$toastr[errType](errMsg, errTitle);
      }

      this.isLoading = false;
    },
    handleOldAndNewPassword(password) {
      if (password !== this.form.oldPassword) {
        return { valid: true };
      }

      return {
        valid: false,
        data: {
          message: "You can't re-use your current password"
        }
      };
    },
    async handleOldPassword(password) {
      try {
        const response = await axios.post("/auth/verify", {
          id: this.$store.state.auth.user.id,
          password
        });

        if (response.status === 200) {
          return { valid: true };
        }

        return { valid: false };
      } catch (error) {
        return {
          valid: false,
          data: {
            message: `The password did not match the old one`
          }
        };
      }
    },
    async handleHasPassword() {
      try {
        const { data } = await axios.get("/auth/verify", {
          params: { id: this.$store.state.auth.user.id }
        });

        this.hasPassword = data.data;
      } catch (error) {
        this.hasPassword = false;
      }
    },
    handleBackButton() {
      this.form = {
        oldPassword: null,
        password: null,
        confirmPassword: null
      };
      this.togglePasswordForm();
      this.$nextTick(() => {
        this.$validator.reset();
      });
    }
  },
  computed: {
    primary() {
      return this.$store.state.app.scheme.primary;
    },
    secondary() {
      return this.$store.state.app.scheme.secondary;
    },
    danger() {
      return this.$store.state.app.scheme.danger;
    },
    event() {
      return this.$store.state.app.scheme.event;
    }
  }
};
</script>

<style lang="scss" scoped>
.form-group {
  position: relative;
  input {
    &:focus {
      box-shadow: none;
      & + span.indicator {
        opacity: 1;
      }
    }
  }
  .invalid-feedback {
    font-family: "Gotham Book";
  }
  span.indicator {
    display: block;
    height: 2px;
    opacity: 0;
    transition: opacity 0.2s ease;
    width: 100%;
  }
}
</style>
